import CloseIcon from '@mui/icons-material/Close';
import {Box, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import moment from 'moment-timezone';
import React, {useMemo, useState, useEffect} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {i18n} from 'src/i18n';
import {CalcResult, calcTotalTareas} from 'src/modules/planificadorTarea/calcTareasCount';

// Misma lógica de ancho que PlanificacionDependencyView
const getDrawerWidth = () =>
  window.innerWidth * 0.5 < 800
    ? window.innerWidth < 800
      ? window.innerWidth
      : 800
    : window.innerWidth * 0.5;

export default function PlanificacionResumenPanel(props: {onClose?: () => void}) {
  const {onClose} = props;
  const [drawerWidth, setDrawerWidth] = useState(getDrawerWidth());

  useEffect(() => {
    const onResize = () => setDrawerWidth(getDrawerWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const espacioRestante = window.innerWidth - drawerWidth;
  const resumenWidth = window.innerWidth * 0.4;
  const useOverlay = espacioRestante < resumenWidth;

  const {control, getValues} = useFormContext();
  const programacionTareas = useWatch({control, name: 'programacionTareas', defaultValue: getValues('programacionTareas')}) || [];

  const {total, byItem} = useMemo(() => calcTotalTareas(programacionTareas), [programacionTareas]);
  const hasUncountableDirigidoA = byItem.some((r) => r?.uncountableBecauseDirigidoA);

  const formatDate = (d: any) => (d ? moment(d).format('DD/MM/YYYY') : '—');

  const getDirigidoALabel = (item: any) => {
    if (!item?.dirigidoA) return '—';
    return i18n(`entities.planificadorTarea.enumerators.dirigidoA.${item.dirigidoA}`);
  };

  const getFechaLimiteLabel = (item: any) => {
    if (!item) return '—';
    const tiempoLimite = item.tiempoLimite;
    const tipoTiempoLimite = item.tipoTiempoLimite || 'days';
    if (tiempoLimite == null || tiempoLimite === '') return '—';
    const n = parseInt(String(tiempoLimite), 10);
    if (isNaN(n)) return '—';
    if (!item.programar) {
      const fechaBase = item.inicioProgramacion || item.fechaEjecucion;
      if (!fechaBase) return '—';
      const fechaLimite = moment(fechaBase).add(n, tipoTiempoLimite).endOf('day');
      return formatDate(fechaLimite);
    }
    const tipoLabel = i18n(`entities.planificadorTarea.enumerators.tipoTiempoLimite.${tipoTiempoLimite}`).toLowerCase();
    return `${n} ${tipoLabel}`;
  };

  const getDetalleLabel = (item: any, result: CalcResult | null) => {
    if (!item) return '—';
    if (result?.reason) return result.reason;
    if (!item.programar) {
      const fecha = item.inicioProgramacion || item.fechaEjecucion;
      return [<span>{`${formatDate(fecha)}`}</span>, <span>{`${result?.cantidadPorEjecucion || 1} tarea(s)`}</span>];
    }
    const inicio = formatDate(item.inicioProgramacion);
    const fin = formatDate(item.finProgramacion);
    const cant = result?.cantidadPorEjecucion ?? 1;
    const tipo = item.tipoPeriodicidad
      ? i18n(`entities.planificadorTarea.enumerators.tipoPeriodicidad.${item.tipoPeriodicidad}`).toLowerCase()
      : 'días';
    const periodo = item.periodicidad ? `cada ${item.periodicidad} ${tipo}` : '';
    const fechasCount = result?.fechasCount ?? 0;
    return [<span>{`${inicio} - ${fin}`}</span>, <span>{`${periodo} | ${cant} por fecha × ${fechasCount} fechas`}</span>];
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        ...(useOverlay
          ? {
              left: window.innerWidth - drawerWidth,
              width: drawerWidth,
            }
          : {
              right: drawerWidth,
              width: Math.min(window.innerWidth * 0.4, espacioRestante),
            }),
        top: 58,
        height: '100vh',
        overflowY: 'auto',
        zIndex: useOverlay ? 1210 : 1,
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        p: 2,
        boxShadow: 2,
      }}>
      <Box sx={{mb: 2}}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
              Resumen de programación
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
              Tareas que se generarán al enviar la planificación
            </Typography>
          </Box>
          {onClose && (
            <Tooltip title="Cerrar resumen">
              <IconButton size="small" onClick={onClose} aria-label="Cerrar resumen">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Paper variant="outlined" sx={{p: 2, mb: 2}}>
        <Typography variant="h4" component="div" sx={{fontWeight: 700}}>
          {hasUncountableDirigidoA ? `${total} + ?` : total}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          tareas en total
          {hasUncountableDirigidoA && (
            <>
              <br />
              <Typography component="span" variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                (de momento solo se puede calcular total dirigido a ejecutor)
              </Typography>
            </>
          )}
        </Typography>
      </Paper>

      <Box sx={{mt: 2}}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
            Por actividad
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
            Desglose por cada item configurado
          </Typography>
        </Box>
        <Table size="small" sx={{mt: 1}}>
          <TableHead>
            <TableRow>
              <TableCell sx={{py: 1, fontWeight: 600, width: 36}}></TableCell>
              <TableCell sx={{py: 1, fontWeight: 600, maxWidth: 180}}>
                {i18n('entities.planificadorTarea.fields.tarea')}
              </TableCell>
              <TableCell sx={{py: 1, fontWeight: 600}}>{i18n('entities.planificadorTarea.fields.dirigidoA')}</TableCell>
              <TableCell sx={{py: 1, fontWeight: 600}}>Fechas</TableCell>
              <TableCell sx={{py: 1, fontWeight: 600}}>Plazos</TableCell>
              <TableCell sx={{py: 1, fontWeight: 600, width: 56}} align="right">
                Subtotal
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programacionTareas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{py: 3}}>
                  <Typography variant="body2" color="text.secondary">
                    Configure las actividades en el formulario
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              programacionTareas.map((item: any, index: number) => {
                const result = byItem[index];
                const titulo = item?.tarea?.titulo || `Actividad ${index + 1}`;
                const order = item?.order ?? index + 1;

                return (
                  <TableRow key={index} sx={{'&:last-child td': {border: 0}}}>
                    <TableCell sx={{width: 36, py: 1}} component="th" scope="row">
                      <Typography variant="body2" fontWeight={600}>
                        {order}.
                      </Typography>
                    </TableCell>
                    <TableCell sx={{py: 1, maxWidth: 180}}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                        {titulo}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{py: 1}}>
                      <Typography variant="caption" color="text.secondary">
                        {getDirigidoALabel(item)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{py: 1}}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}>
                        {getDetalleLabel(item, result)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{py: 1}}>
                      <Typography variant="caption" color="text.secondary">
                        {getFechaLimiteLabel(item)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{py: 1, width: 56}} align="right">
                      {result?.canCalculate ? (
                        <Typography variant="body2" fontWeight={600}>
                          {result.count}
                        </Typography>
                      ) : (
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          ?
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
